import i18n from '../i18n';
import { getCsrfToken } from './csrf';
import { completeJob, failJob, runMeasuredJob, startJob, updateJob, type JobKind } from './jobProgress';

export interface MeasuredFormOpts {
  method?: 'POST' | 'PUT' | 'PATCH';
  headers?: Record<string, string>;
  label?: string;
  kind?: JobKind;
  stayRequired?: boolean;
  requiresOnline?: boolean;
}

interface JsonResponse {
  ok: boolean;
  status: number;
  json: () => Promise<any>;
  text: () => Promise<string>;
}

function parseBody(xhr: XMLHttpRequest): { json: () => Promise<any>; text: () => Promise<string> } {
  const raw = xhr.responseText || '';
  return {
    text: async () => raw,
    json: async () => {
      if (!raw) return {};
      try { return JSON.parse(raw); } catch { return {}; }
    },
  };
}

/** Upload with byte-level progress. `fetch` cannot report upload progress. */
export function postFormWithProgress(url: string, formData: FormData, opts: MeasuredFormOpts): Promise<JsonResponse> {
  const kind = opts.kind || 'upload';
  const jobId = startJob({
    kind,
    label: opts.label || i18n.t(`jobs.kinds.${kind}`),
    stayRequired: opts.stayRequired !== false,
    requiresOnline: opts.requiresOnline !== false,
    total: null,
  });

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(opts.method || 'POST', url);
    xhr.setRequestHeader('X-CSRF-Token', opts.headers?.['X-CSRF-Token'] || getCsrfToken());
    for (const [key, value] of Object.entries(opts.headers || {})) {
      if (key.toLowerCase() === 'content-type' || key.toLowerCase() === 'x-csrf-token') continue;
      xhr.setRequestHeader(key, value);
    }
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) updateJob(jobId, { current: ev.loaded, total: ev.total });
    };
    xhr.onerror = () => {
      failJob(jobId);
      reject(new Error('Network error'));
    };
    xhr.onload = () => {
      const parsed = parseBody(xhr);
      const ok = xhr.status >= 200 && xhr.status < 300;
      if (ok) completeJob(jobId);
      else failJob(jobId);
      resolve({ ok, status: xhr.status, ...parsed });
    };
    xhr.send(formData);
  });
}

/** Download with Content-Length progress when the server sends it. */
export function downloadWithProgress(url: string, filename: string, label: string): Promise<void> {
  const jobId = startJob({
    kind: 'download',
    label: label || i18n.t('jobs.kinds.download'),
    stayRequired: true,
    requiresOnline: true,
    total: null,
  });

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url);
    xhr.responseType = 'blob';
    xhr.setRequestHeader('X-CSRF-Token', getCsrfToken());
    xhr.onprogress = (ev) => {
      if (ev.lengthComputable) updateJob(jobId, { current: ev.loaded, total: ev.total });
    };
    xhr.onerror = () => {
      failJob(jobId);
      reject(new Error('Network error'));
    };
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        failJob(jobId);
        reject(new Error(`HTTP ${xhr.status}`));
        return;
      }
      const blob = xhr.response as Blob;
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(objectUrl);
      completeJob(jobId);
      resolve();
    };
    xhr.send();
  });
}

/** JSON mutation with elapsed time and stay/online warnings. */
export async function fetchJsonWithJob<T = any>(url: string, init: RequestInit, opts: MeasuredFormOpts): Promise<T> {
  const kind = opts.kind || 'other';
  return runMeasuredJob({
    kind,
    label: opts.label || i18n.t(`jobs.kinds.${kind}`),
    stayRequired: opts.stayRequired !== false,
    requiresOnline: opts.requiresOnline !== false,
  }, async () => {
    const headers: Record<string, string> = {
      'X-CSRF-Token': getCsrfToken(),
      ...(init.headers as Record<string, string> | undefined),
    };
    if (init.body && typeof init.body === 'string' && !headers['Content-Type'] && !headers['content-type']) {
      headers['Content-Type'] = 'application/json';
    }
    const res = await fetch(url, { ...init, headers });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data as T;
  });
}

